// React.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { Column, usePagination, useSortBy, useTable } from "react-table";

// Third-party packages.
import * as dayjs from 'dayjs';
import { CSVLink } from 'react-csv';

// Types.
import Filter from '../../../types/Filter';

// Contexts.
import { FilterContext } from '../../../context/FilterContext';

// Components.
import NoData from '../../../component/NoData';
import SortClass from "../../../component/SortClass";
import TablePagination from "../../../component/TablePagination";

// Helpers.
import { getDefaultPageSize, getParameter } from '../../../helpers/utils';
import SearchInput from '../../../component/SearchInput';

export default function List() {
    const { filters } = useContext(FilterContext);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    function getParamValue(filter: Filter, key: string): string | null {
        return getParameter(filter.parameters, key);
    }

    function getFrom(filter: Filter): string | null {
        return getParamValue(filter, 'from');
    }

    function getTo(filter: Filter): string | null {
        return getParamValue(filter, 'to');
    }

    function getTz(filter: Filter): string | null {
        return getParamValue(filter, 'tz');
    }

    function formatDuration(ms: number): string {
        if (ms < 1000) return '0s';

        const seconds = Math.round(ms / 1000);
        const segments = [
            Math.floor(seconds / 86400),       // days
            Math.floor(seconds / 3600) % 24,   // hours
            Math.floor(seconds / 60) % 60,     // minutes
            seconds % 60                       // seconds
        ];
        const units = ['d ', 'h ', 'm ', 's'];

        return segments
                .map((value, i) => (value > 0 ? `${value}${units[i]}` : ''))
                .join('')
                .trim();
    }

    function notNull(value: string | null | undefined): string {
        return value ?? '';
    }

    const tableData = useMemo(() => {
        ReactTooltip.rebuild();
        return filters
                .filter(row => {
                    const search = tableFilter.toLowerCase();
                    return (
                            row.name.toLowerCase().includes(search) ||
                            row.description.toLowerCase().includes(search) ||
                            notNull(getFrom(row)).toLowerCase().includes(search) ||
                            notNull(getTo(row)).toLowerCase().includes(search) ||
                            notNull(getTz(row)).toLowerCase().includes(search)
                    );
                })
                .map(row => {
                    const from = dayjs.default(getFrom(row)).valueOf();
                    const to = dayjs.default(getTo(row)).valueOf();
                    const durationMs = to - from;
                    return {
                        ...row,
                        from: notNull(getFrom(row)),
                        to: notNull(getTo(row)),
                        duration: formatDuration(durationMs),
                        durationMs,
                        timezone: notNull(getTz(row)),
                        keepdata: row.keepdata ? 'Yes' : 'No'
                    };
                })
    }, [filters, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            defaultCanSort: false,
        },
        {
            Header: 'Name',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            Cell: ({ row, value }) => {
                const original = row.original as { description?: string };
                return (
                        <span data-tip={original.description}>
                            {value}
                        </span>
                );
            }
        },
        {
            Header: 'From',
            accessor: 'from',
            defaultCanSort: true,
        },
        {
            Header: 'To',
            accessor: 'to',
            defaultCanSort: true
        },
        {
            Header: 'Duration',
            accessor: 'duration',
            defaultCanSort: true,
            className: 'text-capitalize',
            sortType: (rowA, rowB) => {
                const a = rowA.original as { durationMs: number };
                const b = rowB.original as { durationMs: number };
                return a.durationMs - b.durationMs;
            },
            Cell: ({ cell }) => <>{cell.value}</>
        },
        {
            Header: 'Timezone',
            accessor: 'timezone',
            defaultCanSort: true
        },
        {
            Header: 'Keep Data',
            accessor: 'keepdata',
            defaultCanSort: true
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }) => (
                    <>
                        <Link to={`/admin/filters/edit/${cell.row.values.id}`} role="button"
                              className="btn btn-sm btn-primary">
                            <i className="fal fa-edit fa-fw"/><span>Edit</span>
                        </Link>
                        <Link to={`/admin/filters/delete/${cell.row.values.id}`} role="button"
                              className="btn btn-sm btn-danger">
                            <i className="fal fa-trash-alt fa-fw"/><span>Delete</span>
                        </Link>
                    </>
            )
        }
    ], []);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
                columns,
                data: tableData,
                initialState: {
                    hiddenColumns: ['id'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize,
                    sortBy: [{ id: 'name', desc: false }]
                }
            },
            useSortBy, usePagination);

    ReactTooltip.rebuild();

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);


    return (
            <div className="col h-100">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-camera-alt fa-fw mr-10" aria-hidden="true"></i>
                        Snapshots
                        <span className="badge bg-info"
                              data-tip="Total snapshot count">{filters.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseFilters" aria-expanded="false" aria-controls="collapseFilters"></i>
                    </div>
                    <div id="collapseFilters" className="card-body collapse show">
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <Link to="/admin/filters/create" role="button" className="btn btn-sm btn-success">
                                    <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                </Link>
                                <CSVLink
                                        role="button"
                                        headers={[
                                            { label: 'ID', key: 'id' },
                                            { label: 'Name', key: 'name' },
                                            { label: 'Description', key: 'description' },
                                            { label: 'From', key: 'from' },
                                            { label: 'To', key: 'to' },
                                            { label: 'Duration', key: 'duration' },
                                            { label: 'Timezone', key: 'timezone' },
                                            { label: 'Keep Data', key: 'keepdata' },
                                            { label: 'Parameters', key: 'parameters' }
                                        ]}
                                        data={tableData}
                                        download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Snapshots.csv`}
                                        className="btn btn-sm btn-primary"
                                >
                                    <i className="fal fa-file-export fa-fw"></i><span>Export</span>
                                </CSVLink>
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    ref={inputRef}
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table {...getTableProps()} className="table">
                                <thead>
                                {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        className={(column as any).className}>
                                                        {column.render('Header')}
                                                        <SortClass column={column}/>
                                                    </th>
                                            ))}
                                        </tr>
                                ))}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                {page.map(row => {
                                    prepareRow(row)
                                    return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                            <td {...cell.getCellProps()}
                                                                className={(cell.column as any).className}>
                                                                {cell.render('Cell')}
                                                            </td>
                                                    )
                                                })}
                                            </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                            <NoData
                                    error={null}
                                    loading={false}
                                    length={tableData.length}
                            />
                            <TablePagination
                                    length={tableData.length}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    canPreviousPage={canPreviousPage}
                                    previousPage={previousPage}
                                    canNextPage={canNextPage}
                                    nextPage={nextPage}
                                    gotoPage={gotoPage}
                                    pageIndex={pageIndex}
                            />
                        </div>
                    </div>
                </div>
            </div>
    )
}
